/*
 * Accessibility layer over the compiled Broadcast bundle (theme.js).
 * Loaded with defer after theme.js, so every custom element below is already defined.
 */

(function () {
  const text = document.currentScript.dataset;
  const theme = window.theme || {};
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const FOCUSABLE =
    'a[href], area[href], button, input:not([type="hidden"]), select, textarea, iframe, summary, audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), [tabindex]';

  /* Attributes this file set, so they can be updated or removed without touching the theme's own. */

  const owned = new WeakMap();

  function own(el, name, value) {
    const names = owned.get(el) || new Set();
    if (el.hasAttribute(name) && !names.has(name)) return;
    el.setAttribute(name, value);
    owned.set(el, names.add(name));
  }

  function release(el, name) {
    if (owned.get(el)?.delete(name)) el.removeAttribute(name);
  }

  function releaseAll(el) {
    owned.get(el)?.forEach((name) => el.removeAttribute(name));
    owned.delete(el);
  }

  function deepActive() {
    let el = document.activeElement;
    while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
    return el;
  }

  function within(container, node) {
    for (let current = node; current; current = current.parentNode || current.host) {
      if (current === container) return true;
    }
    return false;
  }

  function isShown(el) {
    return (
      el.isConnected &&
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility === "visible"
    );
  }

  /* Tabs: tablist roles, ids, aria-selected, roving tabindex, arrow keys. */

  const TAB_KEYS = ["ArrowLeft", "ArrowRight", "Home", "End"];
  const enhancedTabs = new WeakSet();

  function ownTabs(component) {
    return Array.from(component.querySelectorAll("[data-tab]")).filter(
      (tab) => tab.closest("tabs-component") === component,
    );
  }

  function tabsScope(component) {
    if (component.id) return component.id;
    const section = component.closest(".shopify-section");
    const index = Array.from((section || document).querySelectorAll("tabs-component")).indexOf(
      component,
    );
    return section ? `${section.id}-tabs-${index}` : `tabs-${index}`;
  }

  function panelFor(component, tab) {
    const controls = tab.getAttribute("aria-controls");
    const byId = controls && document.getElementById(controls);
    if (byId) return byId;

    const key = tab.getAttribute("data-tab");
    return (
      Array.from(component.querySelectorAll(`.tab-content-${CSS.escape(key)}`)).find(
        (panel) => panel.closest("tabs-component") === component,
      ) || null
    );
  }

  function syncTabs(component) {
    const tabs = ownTabs(component);
    const current = tabs.find((tab) => tab.classList.contains("current")) || tabs[0];

    tabs.forEach((tab) => {
      const selected = tab === current;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
  }

  function wireTabs(component) {
    const scope = tabsScope(component);

    ownTabs(component).forEach((tab) => {
      const key = tab.getAttribute("data-tab");
      const list = tab.parentElement;
      if (list && !list.hasAttribute("role")) list.setAttribute("role", "tablist");
      if (!tab.hasAttribute("role")) tab.setAttribute("role", "tab");
      if (!tab.id) tab.id = `${scope}-tab-${key}`;

      const panel = panelFor(component, tab);
      if (!panel) return;

      if (!panel.id) panel.id = `${scope}-panel-${key}`;
      if (!panel.hasAttribute("role")) panel.setAttribute("role", "tabpanel");
      if (!tab.hasAttribute("aria-controls")) tab.setAttribute("aria-controls", panel.id);
      if (!panel.hasAttribute("aria-labelledby")) panel.setAttribute("aria-labelledby", tab.id);
    });
  }

  function onTabKeydown(event) {
    if (!TAB_KEYS.includes(event.key)) return;

    const component = event.currentTarget;
    const tab = event.target.closest("[data-tab]");
    if (!tab || tab.closest("tabs-component") !== component) return;

    const tabs = ownTabs(component).filter((item) => !item.classList.contains("hidden"));
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    const last = tabs.length - 1;
    const next = {
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowRight: index === last ? 0 : index + 1,
      Home: 0,
      End: last,
    }[event.key];

    event.preventDefault();
    tabs[next].click();
    tabs[next].focus();
  }

  function enhanceTabs(component) {
    if (enhancedTabs.has(component)) return;
    enhancedTabs.add(component);

    wireTabs(component);
    syncTabs(component);
    component.addEventListener("keydown", onTabKeydown);
    component.addEventListener("theme:tab:change", () => syncTabs(component));
  }

  /* Carousels: slider-component (Flickity) and the product gallery in fader mode. */

  function markCarousel(root, label) {
    own(root, "role", "region");
    own(root, "aria-roledescription", text.carouselRole);
    if (!root.hasAttribute("aria-labelledby")) own(root, "aria-label", label);
  }

  function markSlides(slides) {
    slides.forEach((slide, index) => {
      if (slide.matches(FOCUSABLE)) return;
      own(slide, "role", "group");
      own(slide, "aria-roledescription", text.slideRole);
      own(
        slide,
        "aria-label",
        text.slideLabel.replace("[index]", index + 1).replace("[count]", slides.length),
      );
    });
  }

  function syncSlides(flkty) {
    const cells = flkty.cells.map((cell) => cell.element);
    markSlides(cells);

    // Flickity sets aria-hidden on unselected cells; the theme only drops links and buttons from the tab order.
    cells.forEach((cell) => {
      const hidden = cell.getAttribute("aria-hidden") === "true";
      cell.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (hidden) own(el, "tabindex", "-1");
        else release(el, "tabindex");
      });
    });

    flkty.pageDots?.dots.forEach((dot, index, dots) => {
      dot.setAttribute(
        "aria-label",
        text.slideLabel.replace("[index]", index + 1).replace("[count]", dots.length),
      );
    });
    flkty.prevButton?.element.setAttribute("aria-label", text.previousLabel);
    flkty.nextButton?.element.setAttribute("aria-label", text.nextLabel);
  }

  const syncedCarousels = new WeakSet();
  let playerGated = false;

  // Every autoplay path (activate, playPlayer, unpause, a tab becoming visible) ends in Player#play.
  function gatePlayer(player) {
    if (playerGated || !player) return;
    playerGated = true;
    const proto = Object.getPrototypeOf(player);
    const { play } = proto;
    proto.play = function () {
      if (!reduceMotion.matches) play.call(this);
    };
  }

  function enhanceCarousel(flkty) {
    gatePlayer(flkty.player);
    if (reduceMotion.matches) flkty.stopPlayer?.();

    const root = flkty.element;
    if (root.localName !== "slider-component") return;

    markCarousel(root, text.slideshowLabel);
    if (!syncedCarousels.has(flkty)) {
      syncedCarousels.add(flkty);
      flkty.on("select", () => syncSlides(flkty));
    }
    syncSlides(flkty);
  }

  function releaseCarousel(flkty) {
    releaseAll(flkty.element);
    flkty.cells.forEach(({ element }) => {
      releaseAll(element);
      element.querySelectorAll(FOCUSABLE).forEach(releaseAll);
    });
  }

  function syncGallery(gallery) {
    const slides = Array.from(gallery.querySelectorAll("[data-image-id]"));
    const fader = theme.isMobile ? "data-fader-mobile" : "data-fader-desktop";

    if (slides.length < 2 || !gallery.hasAttribute(fader)) {
      [gallery, ...slides].forEach(releaseAll);
      return;
    }

    markCarousel(gallery, text.galleryLabel);
    markSlides(slides);
  }

  const Flickity = theme.Flickity;
  const flickities = () =>
    Array.from(document.querySelectorAll(".flickity-enabled"), (el) => Flickity.data(el)).filter(
      Boolean,
    );

  // Sliders are built before this deferred script runs and rebuilt on resize, so hook the shared prototype.
  if (Flickity) {
    const { activate, deactivate } = Flickity.prototype;

    Flickity.prototype.activate = function () {
      activate.call(this);
      if (this.isActive) enhanceCarousel(this);
    };

    Flickity.prototype.deactivate = function () {
      deactivate.call(this);
      releaseCarousel(this);
    };

    flickities().forEach(enhanceCarousel);
  }

  /* Tickers pause through the theme's own ticker-bar[paused] rule. */

  function pauseTicker(ticker) {
    if (reduceMotion.matches) own(ticker, "paused", "");
    else release(ticker, "paused");
  }

  reduceMotion.addEventListener("change", () => {
    document.querySelectorAll("ticker-bar").forEach(pauseTicker);
    if (!Flickity) return;
    flickities().forEach((flkty) => {
      if (reduceMotion.matches) flkty.stopPlayer?.();
      else if (flkty.options.autoPlay) flkty.playPlayer?.();
    });
  });

  /* Desktop nav: the theme writes aria-expanded on the hover-disclosure host, which has no role. */

  const wiredTriggers = new WeakSet();

  function wireNavTrigger(disclosure) {
    const link = disclosure.querySelector("[data-top-link]");
    if (!link || wiredTriggers.has(disclosure)) return;
    wiredTriggers.add(disclosure);

    const move = () => {
      const expanded = disclosure.getAttribute("aria-expanded");
      if (expanded !== null) link.setAttribute("aria-expanded", expanded);
      disclosure.removeAttribute("aria-expanded");
      disclosure.removeAttribute("aria-haspopup");
    };

    // Escape leaves focus on the link, and the theme opens the menu only on focus or hover.
    link.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowDown" || link.getAttribute("aria-expanded") !== "false") return;
      event.preventDefault();
      disclosure.dispatchEvent(new CustomEvent("theme:disclosure:show"));
    });

    move();
    new MutationObserver(move).observe(disclosure, {
      attributeFilter: ["aria-expanded", "aria-haspopup"],
    });
  }

  /* Focus trap: Broadcast computes its first and last tabbables once and only wraps when focus starts on them. */

  const a11y = theme.a11y;
  let trap = null;
  let lastActivated = null;

  document.addEventListener(
    "click",
    (event) => {
      lastActivated = event.target.closest?.(FOCUSABLE) || lastActivated;
    },
    true,
  );

  function isRadioStop(radio) {
    if (!radio.name) return true;
    const scope = radio.form ? radio.form.elements : radio.getRootNode().querySelectorAll("input");
    const group = Array.from(scope).filter(
      (item) => item.type === "radio" && item.name === radio.name,
    );
    return radio === (group.find((item) => item.checked) || group[0]);
  }

  function isTabbable(el) {
    return (
      el.matches(FOCUSABLE) &&
      el.tabIndex >= 0 &&
      !el.matches(":disabled") &&
      (el.type !== "radio" || isRadioStop(el)) &&
      isShown(el)
    );
  }

  function tabbables(container, current) {
    const found = [];
    const visit = (node) => {
      for (const el of node.children) {
        if (el.inert) continue;
        if (el === current || isTabbable(el)) found.push(el);
        if (el.shadowRoot) visit(el.shadowRoot);
        visit(el);
      }
    };

    if (container.shadowRoot) visit(container.shadowRoot);
    visit(container);
    return found;
  }

  // Focus that a pointer moved outside the trap stays there; the next Tab brings it back in.
  function onTrapKeydown(event) {
    if (event.key !== "Tab" || !trap || trap.closing || dropStaleTrap()) return;

    const current = deepActive();
    const items = tabbables(trap.container, current);
    if (!items.length) {
      event.preventDefault();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    let target = null;

    if (!within(trap.container, current)) target = event.shiftKey ? last : first;
    else if (event.shiftKey && (current === first || current === trap.container)) target = last;
    else if (!event.shiftKey && current === last) target = first;

    if (!target) return;
    event.preventDefault();
    target.focus();
  }

  function openerFor(container) {
    return (
      [deepActive(), lastActivated].find(
        (el) => el && el !== document.body && el.isConnected && !within(container, el),
      ) || null
    );
  }

  // Drawers slide in with a visibility transition, and focus() on a still-hidden element is a no-op.
  function focusWhenShown(state, target, options) {
    const deadline = performance.now() + 2000;
    const attempt = () => {
      if (trap !== state) return;
      if (isShown(target) || performance.now() > deadline) a11y.forceFocus(target, options);
      else requestAnimationFrame(attempt);
    };
    attempt();
  }

  // removeTrapFocus also runs between re-traps, so only treat it as a close once the container hides.
  function returnFocusOnClose(state) {
    state.closing = true;
    const deadline = performance.now() + 2000;
    const check = () => {
      if (trap !== state) return;
      if (isShown(state.container) && performance.now() < deadline) {
        requestAnimationFrame(check);
        return;
      }

      trap = null;
      const current = deepActive();
      const lost = !current || current === document.body || within(state.container, current);
      if (lost && !isShown(state.container)) state.opener?.focus();
    };
    requestAnimationFrame(check);
  }

  // A section reload or removal (theme editor) drops the trapped container without removeTrapFocus.
  function dropStaleTrap() {
    if (!trap || trap.container.isConnected) return false;
    a11y.removeTrapFocus();
    return true;
  }

  /* collection.js traps the filter sidebar only in the slide-out layout, where it covers the page: inert the rest. */

  let inerted = [];

  function inertOutside(container) {
    for (
      let node = container;
      node.parentElement && node !== document.body;
      node = node.parentElement
    ) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.inert || sibling.matches(".underlay, script, style"))
          continue;
        sibling.inert = true;
        inerted.push(sibling);
      }
    }
  }

  function releaseInert() {
    inerted.forEach((node) => {
      node.inert = false;
    });
    inerted = [];
  }

  if (a11y) {
    const removeThemeTrap = a11y.removeTrapFocus;

    a11y.removeTrapFocus = () => {
      removeThemeTrap.call(a11y);
      releaseInert();
      document.removeEventListener("keydown", onTrapKeydown, true);
      if (trap && !trap.closing) returnFocusOnClose(trap);
    };

    a11y.trapFocus = (container, options = {}) => {
      const previous = trap;
      a11y.removeTrapFocus();

      const related =
        previous &&
        (within(previous.container, container) || within(container, previous.container));
      trap = {
        container,
        opener: related ? previous.opener : openerFor(container),
        closing: false,
      };

      document.addEventListener("keydown", onTrapKeydown, true);
      if (container.matches("[data-collection-sidebar]")) inertOutside(container);
      focusWhenShown(trap, options.elementToFocus || container, options);
    };
  }

  /* Escape: close the open mega menu and the filter drawer, with focus on their trigger. */

  function closeMegaMenus(focused) {
    document.querySelectorAll("hover-disclosure.is-visible").forEach((disclosure) => {
      if (within(disclosure, focused)) disclosure.querySelector("[data-top-link]")?.focus();
      disclosure.dispatchEvent(new CustomEvent("theme:disclosure:hide"));
    });
  }

  function closeFilters(focused) {
    const sidebar =
      focused?.closest?.("[data-collection-sidebar]") ||
      (trap?.container.matches("[data-collection-sidebar]") ? trap.container : null);
    const component = sidebar?.closest("collection-component");
    const toggle = component?.querySelector("[data-aria-toggle]");
    if (toggle?.getAttribute("aria-expanded") !== "true") return;

    component.dispatchEvent(new CustomEvent("theme:filter:close"));
    toggle.focus();
  }

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const focused = deepActive();
    closeMegaMenus(focused);
    closeFilters(focused);
  });

  /* AJAX filters replace these regions' innerHTML and drop the focused control: focus its counterpart in the new markup. */

  const REPLACED_REGIONS = "[data-collection-sidebar], [data-products-grid], [data-results-count]";
  let regionFocus = null;

  function regionKey(el) {
    const name = el.getAttribute("name");
    if (name && (el.type === "checkbox" || el.type === "radio")) {
      return `${el.localName}[name="${CSS.escape(name)}"][value="${CSS.escape(el.value)}"]`;
    }
    if (name && el.matches("input, select, textarea")) {
      return `${el.localName}[name="${CSS.escape(name)}"]`;
    }
    return el.id ? `#${CSS.escape(el.id)}` : null;
  }

  document.addEventListener("focusin", (event) => {
    const region = event.target.closest?.(REPLACED_REGIONS);
    regionFocus = region ? { el: event.target, region } : null;
  });

  // Chrome can fire focusout on a removed element while it is still connected, so check once the swap is done.
  document.addEventListener("focusout", ({ target }) => {
    queueMicrotask(() => {
      if (target === regionFocus?.el && target.isConnected) regionFocus = null;
    });
  });

  function restoreRegionFocus() {
    if (!regionFocus || regionFocus.el.isConnected) return;
    const { el, region } = regionFocus;
    regionFocus = null;

    const active = document.activeElement;
    if ((active && active !== document.body) || !region.isConnected) return;

    const key = regionKey(el);
    const match = key && region.querySelector(key);
    if (!match || match.matches(":disabled") || !isShown(match)) return;
    const keyboardRing = document.body.classList.contains("is-focused");
    // Keyboard users are scrolled to the control; mouse users keep the theme's scroll to the results.
    match.focus({ preventScroll: !keyboardRing });
    // theme.js reads a focusin over 1s after mousedown as keyboard focus; a slow fetch must not add the ring.
    if (!keyboardRing) document.body.classList.remove("is-focused");

    // Programmatic focus puts the caret at the start, and number inputs reject setSelectionRange.
    if (match.value && match.matches("input:not([type=checkbox], [type=radio]), textarea")) {
      const value = match.value;
      match.value = "";
      match.value = value;
    }
  }

  /* Markup present now and markup added later (section reloads, quick add, AJAX filters). */

  const enhancers = [
    ["tabs-component", enhanceTabs],
    ["product-images", syncGallery],
    ["hover-disclosure", wireNavTrigger],
    ["ticker-bar", pauseTicker],
  ];

  const anyEnhanced = enhancers.map(([selector]) => selector).join(",");

  function enhance(root) {
    enhancers.forEach(([selector, apply]) => {
      if (root.matches(selector)) apply(root);
      root.querySelectorAll(selector).forEach(apply);
    });
  }

  // Most added nodes are text, icons or product cards; one combined check skips them.
  function enhanceAdded(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(anyEnhanced) || node.querySelector(anyEnhanced)) enhance(node);
  }

  enhance(document.body);

  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach(enhanceAdded);
    });
    restoreRegionFocus();
    if (a11y) dropStaleTrap();
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener("theme:resize:width", () => {
    document.querySelectorAll("product-images").forEach(syncGallery);
  });
})();
